import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Upload, X, Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import type { Tables, TablesInsert, TablesUpdate } from '@/lib/supabase/supabase-types';
import { storageService } from '@/lib/api/services/storage.service';
import { useAIWritingAssistant } from '@/lib/hooks/use-ai-writing-assistant';
import { AIAssistantDialog } from '@/components/ai/ai-assistant-dialog';

type PortfolioProject = Tables<'portfolio_projects'>;

interface PortfolioProjectFormProps {
  project?: PortfolioProject;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<TablesInsert<'portfolio_projects'>, 'freelancer_profile_id'>) => Promise<void>;
  onUpdate?: (data: { id: string } & TablesUpdate<'portfolio_projects'>) => Promise<void>;
  isLoading?: boolean;
}

export function PortfolioProjectForm({
  project,
  isOpen,
  onClose,
  onSubmit,
  onUpdate,
  isLoading = false,
}: PortfolioProjectFormProps) {
  const { userId } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectUrl, setProjectUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);

  const { generateProjectDescription, isLoading: isAIGenerating, generatingType, data: aiGeneratedData, reset: resetAIData } = useAIWritingAssistant();

  const isEditing = !!project;

  // Initialize form with project data
  useEffect(() => {
    if (project) {
      setTitle(project.title);
      setDescription(project.description);
      setProjectUrl(project.project_url || '');
      setImageUrl(project.image_url || '');
    } else {
      // Reset form for new project
      setTitle('');
      setDescription('');
      setProjectUrl('');
      setImageUrl('');
    }
  }, [project, isOpen]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userId) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      // Use backend storage API
      const publicUrl = await storageService.uploadFile('portfolio-images', file);
      setImageUrl(publicUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (imageUrl && userId) {
      try {
        // Extract file path from URL
        // Portfolio images are stored in profile-pictures bucket under portfolio/ folder
        const url = new URL(imageUrl);
        // Match both profile-pictures and portfolio-images in URL (for backwards compatibility)
        const pathMatch = url.pathname.match(/\/(?:profile-pictures|portfolio-images)\/(.+)$/);
        if (pathMatch) {
          const filePath = decodeURIComponent(pathMatch[1]);
          await storageService.deleteFile('portfolio-images', filePath);
        }
      } catch (error) {
        console.error('Error removing image:', error);
        // Don't show error to user as this is not critical
      }
    }
    setImageUrl('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('Project title is required');
      return;
    }

    if (!description.trim()) {
      toast.error('Project description is required');
      return;
    }

    try {
      if (isEditing && onUpdate && project) {
        await onUpdate({
          id: project.id,
          title: title.trim(),
          description: description.trim(),
          project_url: projectUrl.trim() || null,
          image_url: imageUrl || null,
        });
      } else {
        await onSubmit({
          title: title.trim(),
          description: description.trim(),
          project_url: projectUrl.trim() || null,
          image_url: imageUrl || null,
        });
      }
      onClose();
    } catch (error) {
      // Error handling is done in the hook
      console.error('Error saving project:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-bold">
            {isEditing ? 'Edit Project' : 'Add New Project'}
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            {isEditing ? 'Update your project details' : 'Add a new project to showcase your AI expertise'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          {/* Project Image */}
          <div className="space-y-3">
            <Label>Project Image (Optional)</Label>
            {imageUrl ? (
              <div className="relative">
                <div className="aspect-video w-full overflow-hidden rounded-lg border">
                  <img
                    src={imageUrl}
                    alt="Project preview"
                    className="h-full w-full object-cover"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveImage}
                  className="absolute right-2 top-2"
                >
                  <X className="h-3 w-3 mr-1" />
                  Remove
                </Button>
              </div>
            ) : (
              <Label
                htmlFor="image-upload"
                className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
              >
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Click to upload an image (PNG, JPG up to 5MB)
                </span>
                {isUploading && (
                  <p className="text-sm text-muted-foreground mt-2">Uploading...</p>
                )}
                <Input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                />
              </Label>
            )}
          </div>

          {/* Project Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Project Title *</Label>
            <Input
              id="title"
              placeholder="AI-Powered Customer Support Bot"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={100}
            />
          </div>

          {/* Project Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="description">Description *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsAIDialogOpen(true)}
                disabled={isAIGenerating}
                className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#2b42a3] hover:bg-[#4669ed]/10"
              >
                {isAIGenerating && generatingType === 'project_description' ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3 w-3 mr-1" />
                    AI Assist
                  </>
                )}
              </Button>
            </div>
            <AIAssistantDialog
              open={isAIDialogOpen}
              onOpenChange={(open) => {
                setIsAIDialogOpen(open);
                if (!open) {
                  resetAIData();
                }
              }}
              onGenerate={async (options) => {
                const result = await generateProjectDescription(description, {
                  ...options,
                  projectTitle: title,
                  projectUrl: projectUrl,
                });
                return result?.text || null;
              }}
              onAccept={(text) => {
                setDescription(text);
                resetAIData();
              }}
              generatedText={aiGeneratedData?.type === 'project_description' ? aiGeneratedData.text : null}
              isLoading={isAIGenerating && generatingType === 'project_description'}
              type="project_description"
              currentText={description}
            />
            <Textarea
              id="description"
              placeholder="Describe your project in detail. Include the problem you solved, your approach, technologies used, and the impact or results achieved..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={10}
              maxLength={2000}
              className="min-h-[200px] resize-y"
            />
            <p className="text-xs text-slate-500">{description.length}/2000 characters</p>
          </div>

          {/* Project URL */}
          <div className="space-y-2">
            <Label htmlFor="projectUrl">Project URL (Optional)</Label>
            <Input
              id="projectUrl"
              type="url"
              placeholder="https://github.com/username/project or https://myproject.com"
              value={projectUrl}
              onChange={(e) => setProjectUrl(e.target.value)}
            />
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-3 sm:gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="w-full sm:w-auto rounded-[48px] order-2 sm:order-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || isUploading}
              className="w-full sm:w-auto rounded-[48px] bg-[#4669ed] hover:bg-[#3654d4] order-1 sm:order-2"
            >
              {isLoading ? 'Saving...' : isEditing ? 'Update Project' : 'Add Project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
